import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import useEmblaCarousel from 'embla-carousel-react'
import slide1Img from '../../assets/img/slide-1.png'
import slide1aCs from '../../assets/img/slide-1-a-cs.png'
import slide1aEn from '../../assets/img/slide-1-a-en.png'
import slide1aUk from '../../assets/img/slide-1-a-uk.png'
import slide1bCs from '../../assets/img/slide-1-b-cs.png'
import slide1bEn from '../../assets/img/slide-1-b-en.png'
import slide1bUk from '../../assets/img/slide-1-b-uk.png'
import slide2Img from '../../assets/img/slide-2.png'
import slide2aCs from '../../assets/img/slide-2-a-cs.png'
import slide2aEn from '../../assets/img/slide-2-a-en.png'
import slide2aUk from '../../assets/img/slide-2-a-uk.png'
import slide2bCs from '../../assets/img/slide-2-b-cs.png'
import slide2bEn from '../../assets/img/slide-2-b-en.png'
import slide2bUk from '../../assets/img/slide-2-b-uk.png'
import slide3Img from '../../assets/img/slide-3.png'
import slide3bCs from '../../assets/img/slide-3-b-cs.png'
import slide3bEn from '../../assets/img/slide-3-b-en.png'
import slide3bUk from '../../assets/img/slide-3-b-uk.png'
import slide4Img from '../../assets/img/slide-4.png'
import slide4bCs from '../../assets/img/slide-4-b-cs.png'
import slide4bEn from '../../assets/img/slide-4-b-en.png'
import slide4bUk from '../../assets/img/slide-4-b-uk.png'
import { useEmblaDots } from '../../hooks/useEmblaDots'
import { ButtonElement, ButtonLocation, trackButtonClick } from '../../utils/analyticsEvents'
import { Button } from '../Button/Button'
import { Icon } from '../Icon/Icon'
import { Modal } from '../Modal/Modal'
import styles from './HowItWorksModal.module.scss'

interface Overlay {
  height: string
  left: string
  radius: number
  top: string
  width: string
}

interface Slide {
  bg: string
  key: string
  legacy: string
  overlays: Overlay[]
  phones: Partial<Record<string, [string, string]>>
}

const SLIDES: Slide[] = [
  {
    key: 'download',
    bg: '#f1e2c0',
    legacy: slide1Img,
    phones: { cs: [slide1aCs, slide1bCs], en: [slide1aEn, slide1bEn], uk: [slide1aUk, slide1bUk] },
    overlays: [],
  },
  {
    key: 'loyalty',
    bg: '#cde1ee',
    legacy: slide2Img,
    phones: { cs: [slide2aCs, slide2bCs], en: [slide2aEn, slide2bEn], uk: [slide2aUk, slide2bUk] },
    overlays: [{ left: '19.86%', top: '3.37%', width: '10.96%', height: '9.74%', radius: 6 }],
  },
  {
    key: 'copy',
    bg: '#c8caf0',
    legacy: slide3Img,
    phones: { cs: [slide2bCs, slide3bCs], en: [slide2bEn, slide3bEn], uk: [slide2bUk, slide3bUk] },
    overlays: [
      { left: '57.53%', top: '42.88%', width: '42.81%', height: '12.73%', radius: 6 },
      { left: '0%', top: '67.98%', width: '21.92%', height: '27.34%', radius: 8 },
    ],
  },
  {
    key: 'create',
    bg: '#c4e9e0',
    legacy: slide4Img,
    phones: { cs: [slide2aCs, slide4bCs], en: [slide2aEn, slide4bEn], uk: [slide2aUk, slide4bUk] },
    overlays: [{ left: '16.1%', top: '87.64%', width: '10.62%', height: '10.86%', radius: 6 }],
  },
]

interface HowItWorksModalProps {
  onClose: () => void
  onGetPromo: () => void
}

export const HowItWorksModal = ({ onClose, onGetPromo }: HowItWorksModalProps) => {
  const { i18n, t } = useTranslation()
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const { onDotClick, selectedIndex } = useEmblaDots(emblaApi)
  const language = i18n.language.split('-')[0].toLowerCase()

  const scrollPrev = useCallback(() => {
    trackButtonClick(ButtonElement.SeeInstruction, ButtonLocation.Instruction)
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    trackButtonClick(ButtonElement.SeeInstruction, ButtonLocation.Instruction)
    emblaApi?.scrollNext()
  }, [emblaApi])

  return (
    <Modal labelledBy="how-modal-title" onClose={onClose}>
      <div className={styles.modal}>
        <h2 className={styles.title} id="how-modal-title">
          {t('howModal.title')}
        </h2>
        <p className={styles.subtitle}>{t('howModal.subtitle')}</p>
        <div ref={emblaRef} className={styles.viewport}>
          <ul className={styles.track}>
            {SLIDES.map((slide, slideIndex) => {
              const phones = slide.phones[language]

              return (
                <li
                  key={slide.key}
                  aria-hidden={slideIndex !== selectedIndex}
                  className={styles.slide}
                >
                  {phones ? (
                    <div className={styles.media} style={{ backgroundColor: slide.bg }}>
                      <div className={styles.row}>
                        <span className={styles.phone}>
                          <img alt="" src={phones[0]} />
                        </span>
                        <Icon className={styles.rowArrow} name="arrow-right" size={16} />
                        <span className={styles.phone}>
                          <img alt="" src={phones[1]} />
                        </span>
                        {slide.overlays.map((overlay) => (
                          <span
                            key={`${overlay.left}-${overlay.top}`}
                            className={styles.overlay}
                            style={{
                              borderRadius: overlay.radius,
                              height: overlay.height,
                              left: overlay.left,
                              top: overlay.top,
                              width: overlay.width,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <img
                      alt={t(`howModal.slides.${slide.key}.title`)}
                      className={styles.image}
                      height="285"
                      src={slide.legacy}
                      width="438"
                    />
                  )}
                  <div className={styles.caption}>
                    <span aria-hidden className={styles.number}>
                      {slideIndex + 1}
                    </span>
                    <div>
                      <h3 className={styles.captionTitle}>
                        {t(`howModal.slides.${slide.key}.title`)}
                      </h3>
                      <p className={styles.captionText}>{t(`howModal.slides.${slide.key}.text`)}</p>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
        <div className={styles.controls}>
          <button
            aria-label={t('howModal.prev')}
            className={styles.arrow}
            type="button"
            onClick={scrollPrev}
          >
            <Icon className={styles.prev} name="chevron-right" size={24} />
          </button>
          <div className={styles.dots}>
            {SLIDES.map((slide, dotIndex) => (
              <button
                key={slide.key}
                aria-current={dotIndex === selectedIndex}
                aria-label={t('howModal.step', { number: dotIndex + 1 })}
                className={`${styles.dot}${dotIndex === selectedIndex ? ` ${styles.active}` : ''}`}
                type="button"
                onClick={() => {
                  onDotClick(dotIndex)
                }}
              />
            ))}
          </div>
          <button
            aria-label={t('howModal.next')}
            className={styles.arrow}
            type="button"
            onClick={scrollNext}
          >
            <Icon name="chevron-right" size={24} />
          </button>
        </div>
        <div className={styles.footer}>
          <Button
            variant="green"
            onClick={() => {
              trackButtonClick(ButtonElement.GetPromocode, ButtonLocation.Instruction)
              onGetPromo()
            }}
          >
            {t('howModal.cta')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
